/**
 * Client Timber Summary
 * ---------------------
 * Pure helpers that turn an internal `TimberListItem[]` into a privacy-safe
 * summary suitable for the public proposal page.
 *
 * The client-facing view only ever exposes:
 *   - Piece name (e.g. "Rafter")
 *   - Grade
 *   - Dryness (green / kiln-dried / unspecified)
 *   - Cross-section ONLY — the first two of the three numbers in `dimensions`.
 *     "8x8x14" becomes "8x8". Length is intentionally dropped because it's
 *     considered pricing-relevant takeoff detail.
 *
 * Quantities, board-feet, unit costs, totals, and per-line ids are all
 * omitted. Rows that share `(name, grade, dryness, cross-section)` collapse
 * into a single entry — the user explicitly opted for a deduped, no-quantity
 * "sizes used" list rather than a takeoff table.
 */

import type { TimberListItem } from '../types';

/** A single row in the client-safe timber summary. */
export interface ClientTimberRow {
  /** Stable composite key — useful as a React `key`. */
  key: string;
  /** Piece name (e.g. "Rafter", "Brace"). Empty pieces are dropped upstream. */
  name: string;
  /** Grade as entered (e.g. "#1", "Select"). Empty values render as a dash. */
  grade: string;
  /** Pretty dryness label ready for display. See {@link prettyDryness}. */
  dryness: string;
  /** Cross-section, e.g. "4x8" or "7.5x8" when the line keeps true dimensions. */
  crossSection: string;
}

/**
 * Pull the first two numeric segments from a dimension string. Returns null
 * when the string doesn't have at least two parseable numbers — those rows
 * are excluded from the client summary entirely so the table never shows a
 * confusing partial entry.
 *
 * Tolerated forms (real data observed in the codebase):
 *   "8x8x14"    -> "8x8"
 *   "4×8×12"    -> "4x8"
 *   "8X8X14'"   -> "8x8"
 *   " 4x8x12 "  -> "4x8"
 *
 * Each segment is run through `[^0-9.]` stripping so trailing units like
 * `'`, `"`, or `ft` don't break the parse.
 */
export function parseCrossSection(
  dim: string | undefined | null,
  trueDimensions = false,
): string | null {
  if (!dim) return null;
  const parts = dim
    .toString()
    .split(/[x×]/i)
    .map(p => p.replace(/[^0-9.]/g, '').trim())
    .filter(p => p.length > 0);
  if (parts.length < 2) return null;
  // Unchecked lists round to the nearest inch. True-dimension lines keep 7.5.
  const toInches = (raw: string | undefined) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return NaN;
    return trueDimensions ? Math.round(n * 10000) / 10000 : Math.round(n);
  };
  const a = toInches(parts[0]);
  const b = toInches(parts[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return `${a}x${b}`;
}

/** Map the internal moisture flag to a human label for the client view. */
export function prettyDryness(m: TimberListItem['moistureContent']): string {
  if (m === 'green') return 'Green';
  if (m === 'kiln_dried') return 'Kiln-dried';
  return '—';
}

/** Pretty grade — collapses empty / whitespace-only entries to a dash. */
function prettyGrade(g: string): string {
  const t = (g || '').trim();
  return t.length > 0 ? t : '—';
}

/**
 * Sort key for cross-sections — sorts by the larger nominal first, then the
 * smaller, so "8x8" beats "6x8" beats "4x8" beats "4x6". Matches the way
 * timber framers usually list members (heavy → light).
 */
function crossSectionSortKey(cs: string): [number, number] {
  const [a, b] = cs.split('x').map(n => Number(n) || 0);
  return [Math.max(a, b), Math.min(a, b)];
}

/**
 * Build a deduped, client-safe summary from a structure's `materials.timberList`.
 *
 * Lines that share `(name, grade, dryness, cross-section)` collapse into a
 * single row — different lengths of the same piece (e.g. "Rafter 4x8x12",
 * "Rafter 4x8x14") become one "Rafter · #1 · Kiln-dried · 4x8" entry.
 *
 * Returns rows sorted heaviest-cross-section first, then by piece name.
 * Empty lists, or lists where every line failed `parseCrossSection`, return
 * an empty array — the caller should hide the "View timber sizes" affordance
 * in that case.
 */
export function summarizeTimbersForClient(list: TimberListItem[] | undefined | null): ClientTimberRow[] {
  if (!list || list.length === 0) return [];
  const seen = new Map<string, ClientTimberRow>();
  for (const item of list) {
    const cs = parseCrossSection(item.dimensions, item.trueDimensions === true);
    if (!cs) continue;
    const name = (item.name || '').trim();
    if (!name) continue;
    const grade = prettyGrade(item.grade);
    const dryness = prettyDryness(item.moistureContent);
    // Composite key uses lowercase / normalized fragments so trivial casing
    // differences don't leak duplicates ("Rafter" + "rafter" → one row).
    const key = `${name.toLowerCase()}|${grade.toLowerCase()}|${dryness.toLowerCase()}|${cs}`;
    if (seen.has(key)) continue;
    seen.set(key, { key, name, grade, dryness, crossSection: cs });
  }
  return Array.from(seen.values()).sort((a, b) => {
    const [aMax, aMin] = crossSectionSortKey(a.crossSection);
    const [bMax, bMin] = crossSectionSortKey(b.crossSection);
    if (aMax !== bMax) return bMax - aMax;
    if (aMin !== bMin) return bMin - aMin;
    return a.name.localeCompare(b.name);
  });
}
